import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "preact/compat";
import { Popup, Toast } from "react-vant";
import { useLocation } from "react-router-dom";
import FloatingBall from "@/components/DraggableFloatingBall";
import { LocalConfig } from "@/config";
import { PersistContext } from "@/data/PersistProvider";
import { useEnhancedHistory } from "@/data/RouterProvider";
import { GetDownloadApkUrl } from "@/data/urls";
import { SendCMD } from "@/utils/HTTPRequest";
import { consumeCustomerServiceExternalGameReturn, CUSTOMER_SERVICE_OPEN_EVENT } from "@/utils/CustomerService";
import { ChannelRemind } from "@/utils/UrlHelper";
import { FromGameApp, FromStandAlone } from "@/utils/BroswerHelper";
import style from "./style.less";

const CHAT_SEND_CMD = "customerChatSend";
const CHAT_HISTORY_CMD = "customerChatHistory";
const CHAT_HISTORY_BEFORE_CMD = "customerChatHistoryBefore";
const CHAT_IMAGE_UPLOAD_CMD = "customerChatImageUpload";
const POLL_INTERVAL = 5000;
const HISTORY_LIMIT = 20;
const MAX_UNREPLIED_PLAYER_MESSAGES = 10;
const MESSAGE_TYPE_TEXT = 0;
const MESSAGE_TYPE_IMAGE = 1;
const CHAT_CACHE_VERSION = 3;
const CHAT_CACHE_LIMIT = 100;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const CustomerServiceFloatIcon = require("@/assets/images/service/icon_kefu.png").default;
const CopyIcon = require("@/assets/icons/copy.png").default;
const PARTNER_GAME_MODE_CLASS = "partner-game-mode";
const SESSION_EXPIRED_MESSAGE = "Your account was logged in elsewhere. Please login again.";
const AUTH_ERROR_KEYWORDS = [
  "token",
  "login",
  "log in",
  "session",
  "expired",
  "expire",
  "invalid",
  "unauthorized",
  "forbidden",
  "elsewhere",
  "another device",
  "other device",
  "kicked",
];

const getCssPixelValue = (propertyName, fallback) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return fallback;
  }

  const value = window.getComputedStyle(document.documentElement).getPropertyValue(propertyName);
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const isPartnerGameMode = () => {
  if (typeof document === "undefined") {
    return false;
  }
  return (
    document.documentElement.classList.contains(PARTNER_GAME_MODE_CLASS) ||
    document.body.classList.contains(PARTNER_GAME_MODE_CLASS)
  );
};

const getCustomerServiceFloatOffset = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return {
      right: "1rem",
      bottom: "6rem",
    };
  }

  const viewportWidth = document.documentElement.clientWidth || window.innerWidth || window.screen.width;
  const partnerGameMode = isPartnerGameMode();
  const rootFontSize = getCssPixelValue("font-size", window.rem || 16);
  let rightGap = 0;

  if (!partnerGameMode) {
    const appElement = document.getElementById("app") || document.getElementById("preact_root");
    const appRect = appElement && appElement.getBoundingClientRect();

    if (appRect && appRect.width) {
      rightGap = Math.max(viewportWidth - appRect.right, 0);
    } else {
      const appWidth = window.appViewportWidth || viewportWidth;
      rightGap = Math.max((viewportWidth - appWidth) / 2, 0);
    }
  }

  return {
    right: `${Math.round(rightGap + rootFontSize)}px`,
    bottom: partnerGameMode ? "1rem" : "6rem",
  };
};

const copyTextToClipboard = async (text) => {
  if (!text || typeof document === "undefined") {
    return false;
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.warn("navigator clipboard failed", error);
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } catch (error) {
    console.warn("execCommand copy failed", error);
  }
  document.body.removeChild(textarea);
  return copied;
};

const getClientConfig = (bgSettings) => {
  if (bgSettings && bgSettings.settings && bgSettings.settings.client_config) {
    return bgSettings.settings.client_config;
  }
  return {};
};

const getCustomerServiceInfo = (bgSettings, userInfo) => {
  const clientConfig = getClientConfig(bgSettings);
  const serviceConfig = clientConfig.customerService || clientConfig.customer_service || {};
  const userName = (userInfo && (userInfo.nick || userInfo.name || userInfo.rid)) || LocalConfig.gameName;

  return {
    contactType: serviceConfig.contactType || clientConfig.contactType || "Bot",
    userName,
  };
};

const getChatCacheKey = (userInfo) => {
  if (!userInfo || !userInfo.token) {
    return "";
  }
  return `customer_chat_cache_${encodeURIComponent(userInfo.rid || userInfo.token)}`;
};

const normalizeSenderType = (senderType) => {
  if (senderType === null || typeof senderType === "undefined") {
    return -1;
  }

  const senderTypeText = String(senderType).trim();
  if (!senderTypeText) {
    return -1;
  }

  const numericSenderType = Number(senderTypeText);
  if (!Number.isNaN(numericSenderType)) {
    return numericSenderType;
  }

  const typeText = senderTypeText.toLowerCase();
  if (["player", "user", "member", "me", "mine"].includes(typeText)) {
    return 0;
  }
  if (["customer", "service", "admin", "agent", "cs"].includes(typeText)) {
    return 1;
  }
  if (typeText === "system") {
    return 2;
  }
  return -1;
};

const normalizeIdentityValue = (value) => {
  if (value === null || typeof value === "undefined") {
    return "";
  }
  return String(value).trim().toLowerCase();
};

const getCurrentUserIdentitySet = (userInfo) => {
  if (!userInfo) {
    return new Set();
  }

  return new Set(
    [
      userInfo.rid,
      userInfo.id,
      userInfo.uid,
      userInfo.player_id,
      userInfo.playerId,
      userInfo.rolename,
      userInfo.role_name,
      userInfo.role_account,
      userInfo.account,
      userInfo.phone,
      userInfo.mobile,
      userInfo.nick,
      userInfo.name,
    ]
      .map(normalizeIdentityValue)
      .filter(Boolean)
  );
};

const isMyMessage = (message, userInfo) => {
  if (!message) {
    return false;
  }
  const senderType = normalizeSenderType(message.sender_type);
  if (senderType === 0) {
    return true;
  }
  if (senderType === 1 || senderType === 2) {
    return false;
  }

  const userIdentitySet = getCurrentUserIdentitySet(userInfo);
  if (!userIdentitySet.size) {
    return false;
  }

  return [
    message.sender_id,
    message.sender_name,
    message.sender_rid,
    message.sender_uid,
    message.sender_rolename,
    message.sender_role_name,
    message.sender_role_account,
    message.rid,
    message.uid,
    message.player_id,
    message.playerId,
    message.rolename,
    message.role_name,
    message.role_account,
    message.account,
    message.phone,
    message.mobile,
  ]
    .map(normalizeIdentityValue)
    .some((value) => value && userIdentitySet.has(value));
};

const parseJsonIfNeeded = (value) => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const parseCustomerChatResponse = (res) => {
  if (!res) {
    return {
      ok: false,
      data: {},
      error: "Request failed",
    };
  }
  if (res.status === false) {
    return {
      ok: false,
      data: {},
      error: res.errdesc || "Request failed",
      code: res.errdcode,
    };
  }
  if (res.status === true) {
    return {
      ok: true,
      data: parseJsonIfNeeded(res.data) || {},
    };
  }
  return {
    ok: true,
    data: parseJsonIfNeeded(res.data || res.msg || res),
  };
};

const getPlatform = () => {
  if (FromGameApp) {
    return "2";
  }
  if (FromStandAlone) {
    return "3";
  }
  return "1";
};

const isAuthExpiredText = (text) => {
  if (!text) {
    return false;
  }
  const normalizedText = String(text).toLowerCase();
  return AUTH_ERROR_KEYWORDS.some((keyword) => normalizedText.includes(keyword));
};

const isAuthExpiredResponse = (response) => {
  if (!response) {
    return false;
  }
  if (response.response && (response.response.status === 401 || response.response.status === 403)) {
    return true;
  }

  const responseData = response.response ? response.response.data : response;
  if (!responseData) {
    return false;
  }
  if (responseData.status === 401 || responseData.status === 403) {
    return true;
  }
  return isAuthExpiredText(responseData.code || responseData.errdcode || responseData.error || responseData.errdesc || responseData.message);
};

const getMessageId = (message) => {
  if (typeof message.msg_id !== "undefined") {
    return Number(message.msg_id);
  }
  if (typeof message.id !== "undefined") {
    return Number(message.id);
  }
  return 0;
};

const normalizeTimestamp = (timestamp) => {
  if (!timestamp) {
    return Math.floor(Date.now() / 1000);
  }
  return Number(timestamp) > 100000000000 ? Math.floor(Number(timestamp) / 1000) : Number(timestamp);
};

const isImageContent = (content) => /\.(png|jpe?g)(?:$|[?#])/i.test(String(content || ""));

const normalizeMessageType = (msgType, content) => {
  if (msgType === null || typeof msgType === "undefined" || String(msgType).trim() === "") {
    return isImageContent(content) ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
  }

  const typeText = String(msgType).trim().toLowerCase();
  const numericType = Number(typeText);
  if (!Number.isNaN(numericType)) {
    return numericType === MESSAGE_TYPE_IMAGE ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
  }
  if (["image", "img", "photo", "picture"].includes(typeText)) {
    return MESSAGE_TYPE_IMAGE;
  }
  return isImageContent(content) ? MESSAGE_TYPE_IMAGE : MESSAGE_TYPE_TEXT;
};

const isImageMessage = (message) => normalizeMessageType(message && message.msg_type, message && message.content) === MESSAGE_TYPE_IMAGE;

const normalizeMessage = (message) => ({
  msg_id: getMessageId(message),
  sender_type: normalizeSenderType(message.sender_type),
  sender_id: message.sender_id || message.sender_rid || message.rid || message.sender_uid || message.uid || message.player_id || message.playerId || "",
  sender_name: message.sender_name || message.sender_rolename || message.rolename || message.sender_role_name || message.role_name || message.account || "",
  content: message.content || "",
  msg_type: normalizeMessageType(message.msg_type, message.content),
  extra: message.extra || "",
  timestamp: normalizeTimestamp(message.timestamp),
  status: "sent",
});

const getHistoryMessages = (data) => {
  const parsedData = parseJsonIfNeeded(data) || {};
  let messages = parseJsonIfNeeded(Array.isArray(parsedData) ? parsedData : parsedData.messages);

  if (!messages) {
    return [];
  }
  if (Array.isArray(messages)) {
    return messages;
  }
  if (typeof messages === "object") {
    const nestedMessages = parseJsonIfNeeded(messages.messages || messages.list || messages.items || messages.data);
    if (Array.isArray(nestedMessages)) {
      return nestedMessages;
    }
    return Object.values(messages).filter(
      (item) =>
        item &&
        typeof item === "object" &&
        (typeof item.msg_id !== "undefined" || typeof item.id !== "undefined" || typeof item.content !== "undefined")
    );
  }
  return [];
};

const isHasMore = (value) => value === true || value === 1 || value === "1" || value === "true";

const getResponseHasMore = (value, messageCount) => {
  if (typeof value === "undefined") {
    return messageCount >= HISTORY_LIMIT;
  }
  return isHasMore(value);
};

const sortMessages = (messages) =>
  [...messages].sort((a, b) => {
    const aMsgId = getMessageId(a);
    const bMsgId = getMessageId(b);
    if (aMsgId && bMsgId && aMsgId !== bMsgId) {
      return aMsgId - bMsgId;
    }
    if (a.timestamp !== b.timestamp) {
      return a.timestamp - b.timestamp;
    }
    return (aMsgId || a.client_time || 0) - (bMsgId || b.client_time || 0);
  });

const mergeMessages = (oldMessages, newMessages) => {
  const merged = [...oldMessages];

  newMessages.forEach((message) => {
    const serverId = getMessageId(message);
    const clientTime = message.client_time ? Number(message.client_time) : 0;
    const existingIndex = merged.findIndex((item) => {
      if (serverId && item.msg_id === serverId) {
        return true;
      }
      if (clientTime && item.client_time === clientTime) {
        return true;
      }
      return false;
    });

    if (existingIndex > -1) {
      merged[existingIndex] = {
        ...merged[existingIndex],
        ...message,
        msg_id: serverId || merged[existingIndex].msg_id,
        timestamp: normalizeTimestamp(message.timestamp || merged[existingIndex].timestamp),
        status: message.status || "sent",
      };
    } else {
      merged.push(message);
    }
  });

  return sortMessages(merged);
};

const getMaxMessageId = (messages) =>
  messages.reduce((maxId, message) => {
    const msgId = getMessageId(message);
    return msgId > maxId ? msgId : maxId;
  }, 0);

const getMinMessageId = (messages) =>
  messages.reduce((minId, message) => {
    const msgId = getMessageId(message);
    if (!msgId) {
      return minId;
    }
    return minId === 0 || msgId < minId ? msgId : minId;
  }, 0);

const getCacheableMessages = (messages) =>
  sortMessages(
    messages.filter((message) => {
      if (message.status !== "sent" || !getMessageId(message)) {
        return false;
      }
      if (!message.content || String(message.content).indexOf("blob:") === 0) {
        return false;
      }
      return true;
    })
  )
    .slice(-CHAT_CACHE_LIMIT)
    .map((message) => ({
      msg_id: getMessageId(message),
      sender_type: normalizeSenderType(message.sender_type),
      sender_id: message.sender_id || "",
      sender_name: message.sender_name || "",
      content: message.content || "",
      msg_type: normalizeMessageType(message.msg_type, message.content),
      extra: message.extra || "",
      timestamp: normalizeTimestamp(message.timestamp),
      status: "sent",
    }));

const readChatCache = (cacheKey) => {
  if (!cacheKey) {
    return [];
  }
  try {
    const cacheText = localStorage.getItem(cacheKey);
    if (!cacheText) {
      return [];
    }
    const cache = JSON.parse(cacheText);
    if (!cache || cache.version !== CHAT_CACHE_VERSION || !Array.isArray(cache.messages)) {
      return [];
    }
    return sortMessages(cache.messages.map(normalizeMessage));
  } catch (error) {
    console.error("read customer chat cache failed", error);
    return [];
  }
};

const writeChatCache = (cacheKey, messages) => {
  if (!cacheKey) {
    return;
  }
  try {
    const cacheMessages = getCacheableMessages(messages);
    localStorage.setItem(
      cacheKey,
      JSON.stringify({
        version: CHAT_CACHE_VERSION,
        max_msg_id: getMaxMessageId(cacheMessages),
        updated_at: Date.now(),
        messages: cacheMessages,
      })
    );
  } catch (error) {
    console.error("write customer chat cache failed", error);
  }
};

const countUnrepliedPlayerMessages = (messages, userInfo) => {
  let count = 0;
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    const mine = isMyMessage(message, userInfo);
    if (!mine && normalizeSenderType(message.sender_type) !== 2) {
      break;
    }
    if (mine && message.status !== "failed") {
      count++;
    }
  }
  return count;
};

const formatMessageTime = (timestamp) => {
  const date = new Date(normalizeTimestamp(timestamp) * 1000);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const getFileExtension = (file) => {
  const name = (file && file.name ? file.name : "").toLowerCase();
  const type = (file && file.type ? file.type : "").toLowerCase();
  if (name.endsWith(".png") || type === "image/png" || type === "image/x-png") {
    return "png";
  }
  if (
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".jfif") ||
    type === "image/jpeg" ||
    type === "image/jpg" ||
    type === "image/pjpeg"
  ) {
    return "jpg";
  }
  return "";
};

const getImageContentType = (file, fileExtension) => {
  const type = (file && file.type ? file.type : "").toLowerCase();
  if (type === "image/png" || type === "image/x-png" || fileExtension === "png") {
    return "image/png";
  }
  if (type === "image/jpeg" || type === "image/jpg" || type === "image/pjpeg" || fileExtension === "jpg") {
    return "image/jpeg";
  }
  return type;
};

const normalizeUploadUrl = (url) => {
  if (typeof window === "undefined" || window.location.protocol !== "https:" || !url || url.indexOf("http://") !== 0) {
    return url;
  }
  return `https://${url.slice("http://".length)}`;
};

const createUploadBodyWithoutType = (file) => {
  try {
    return new Blob([file], { type: "" });
  } catch (error) {
    console.warn("create upload body fallback failed", error);
    return file;
  }
};

const putImageByFetch = (url, body, contentType) => {
  const options = {
    method: "PUT",
    body,
  };
  if (contentType) {
    options.headers = {
      "Content-Type": contentType,
    };
  }
  return fetch(url, options);
};

const putImageByXhr = (url, body, contentType) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    xhr.timeout = 60000;
    if (contentType) {
      xhr.setRequestHeader("Content-Type", contentType);
    }
    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
      });
    };
    xhr.onerror = reject;
    xhr.ontimeout = reject;
    xhr.send(body);
  });

const putImageToUploadUrl = async (url, file, contentType) => {
  const attempts = [
    { name: "fetch-content-type", uploader: putImageByFetch, body: file, contentType },
    { name: "fetch-no-content-type", uploader: putImageByFetch, body: createUploadBodyWithoutType(file), contentType: "" },
    { name: "xhr-content-type", uploader: putImageByXhr, body: file, contentType },
    { name: "xhr-no-content-type", uploader: putImageByXhr, body: createUploadBodyWithoutType(file), contentType: "" },
  ];
  let lastResponse = null;
  let lastError = null;

  for (const attempt of attempts) {
    try {
      const response = await attempt.uploader(url, attempt.body, attempt.contentType);
      if (response && response.ok) {
        return response;
      }
      lastResponse = response;
      console.warn("customer chat image upload attempt failed", attempt.name, response && response.status);
    } catch (error) {
      lastError = error;
      console.warn("customer chat image upload attempt error", attempt.name, error);
    }
  }

  if (lastResponse) {
    return lastResponse;
  }
  throw lastError || new Error("Image upload failed");
};

const getImageSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve({});
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });

const getImagePreview = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CustomerServiceWidget = () => {
  const { getPersist, setPersist } = useContext(PersistContext);
  const history = useEnhancedHistory();
  const location = useLocation();
  const userInfo = getPersist("user_info");
  const userToken = userInfo && userInfo.token;
  const chatCacheKey = useMemo(() => getChatCacheKey(userInfo), [userInfo]);
  const [visible, setVisible] = useState(false);
  const [gameMode, setGameMode] = useState(false);
  const [portalContainer, setPortalContainer] = useState(null);
  const [floatBallOffset, setFloatBallOffset] = useState(getCustomerServiceFloatOffset);
  const [floatBallResetKey, setFloatBallResetKey] = useState(0);
  const floatBallOffsetRef = useRef(floatBallOffset);
  const [downloadApkUrl, setDownloadApkUrl] = useState("");
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const pollTimerRef = useRef(null);
  const loadingHistoryRef = useRef(false);
  const loadingHistoryBeforeRef = useRef(false);
  const fileInputRef = useRef(null);
  const chatBodyRef = useRef(null);
  const messagesEndRef = useRef(null);
  const maxMsgIdRef = useRef(0);
  const minMsgIdRef = useRef(0);
  const hasMoreBeforeRef = useRef(true);
  const skipNextScrollToBottomRef = useRef(false);
  const skipNextCacheWriteRef = useRef(false);
  const chatServiceUnavailableRef = useRef(false);
  const authRedirectingRef = useRef(false);

  const unrepliedCount = useMemo(() => countUnrepliedPlayerMessages(messages, userInfo), [messages, userInfo]);
  const sendLimited = unrepliedCount >= MAX_UNREPLIED_PLAYER_MESSAGES;
  const sendDisabled = sending || uploading || sendLimited;
  const inputDisabled = sending || uploading;

  const updateFloatBallOffset = useCallback((forceReset = false) => {
    const nextOffset = getCustomerServiceFloatOffset();
    const prevOffset = floatBallOffsetRef.current;
    const hasChanged = prevOffset.right !== nextOffset.right || prevOffset.bottom !== nextOffset.bottom;

    if (hasChanged) {
      floatBallOffsetRef.current = nextOffset;
      setFloatBallOffset(nextOffset);
    }
    if (hasChanged || forceReset) {
      setFloatBallResetKey((key) => key + 1);
    }
  }, []);

  const syncGameMode = useCallback(() => {
    setGameMode(isPartnerGameMode());
    updateFloatBallOffset();
  }, [updateFloatBallOffset]);

  const buildRequest = useCallback(
    (payload) => {
      if (userInfo && userInfo.token) {
        return { ...payload, token: userInfo.token };
      }
      return payload;
    },
    [userInfo]
  );

  const redirectToLogin = useCallback(
    (message = SESSION_EXPIRED_MESSAGE) => {
      if (authRedirectingRef.current) {
        return;
      }

      authRedirectingRef.current = true;
      const backUrl = location.pathname + location.search;
      setPersist("login_back_url", backUrl);
      setPersist("user_info", null);
      setPersist("gatesvr", null);
      setVisible(false);
      chatServiceUnavailableRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      Toast.info(message);
      history.replace(
        ChannelRemind(`/login?${getPersist("last_phone") ? "" : "reg=1&"}back_to=${encodeURIComponent(backUrl)}`)
      );
    },
    [getPersist, history, location.pathname, location.search, setPersist]
  );

  const verifyCurrentSession = useCallback(async () => {
    const currentUserInfo = getPersist("user_info");
    if (!currentUserInfo || !currentUserInfo.token) {
      redirectToLogin();
      return false;
    }

    try {
      const res = await SendCMD("getMe", {
        plat: getPlatform(),
        token: currentUserInfo.token,
      });
      if (!res || !res.status) {
        redirectToLogin();
        return false;
      }
      if (res.player_info) {
        setPersist("user_info", res.player_info);
        setPersist("gatesvr", res.gatesvr);
      }
      return true;
    } catch (error) {
      console.error("customer service session verify failed", error);
      return true;
    }
  }, [getPersist, redirectToLogin, setPersist]);

  const handleSessionExpired = useCallback(
    async (source) => {
      if (authRedirectingRef.current) {
        return true;
      }
      if (isAuthExpiredResponse(source)) {
        redirectToLogin();
        return true;
      }
      if (source && source.ok === false) {
        return !(await verifyCurrentSession());
      }
      return false;
    },
    [redirectToLogin, verifyCurrentSession]
  );

  useEffect(() => {
    if (userToken) {
      authRedirectingRef.current = false;
    }
    skipNextCacheWriteRef.current = true;
    setInputValue("");
    setLoadingHistory(false);
    setSending(false);
    setUploading(false);
    loadingHistoryRef.current = false;
    loadingHistoryBeforeRef.current = false;
    skipNextScrollToBottomRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (!chatCacheKey) {
      setMessages([]);
      maxMsgIdRef.current = 0;
      minMsgIdRef.current = 0;
      hasMoreBeforeRef.current = true;
      setHistoryLoaded(false);
      setVisible(false);
      return;
    }

    const cachedMessages = readChatCache(chatCacheKey);
    setMessages(cachedMessages);
    maxMsgIdRef.current = getMaxMessageId(cachedMessages);
    minMsgIdRef.current = getMinMessageId(cachedMessages);
    hasMoreBeforeRef.current = true;
    setHistoryLoaded(cachedMessages.length > 0);
  }, [chatCacheKey, userToken]);

  useEffect(() => {
    setPortalContainer(document.body);
  }, []);

  useEffect(() => {
    let mounted = true;

    Promise.resolve(GetDownloadApkUrl("")).then((url) => {
      if (mounted) {
        setDownloadApkUrl(url);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    syncGameMode();

    if (typeof MutationObserver === "undefined") {
      window.addEventListener("resize", syncGameMode);
      return () => {
        window.removeEventListener("resize", syncGameMode);
      };
    }

    const observer = new MutationObserver(syncGameMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

    return () => {
      observer.disconnect();
    };
  }, [location.pathname, location.search, syncGameMode]);

  useEffect(() => {
    const scheduledTimers = [];
    const updateFloatOffset = () => {
      updateFloatBallOffset(true);
      scheduledTimers.forEach((timer) => window.clearTimeout(timer));
      scheduledTimers.length = 0;
      scheduledTimers.push(window.setTimeout(updateFloatBallOffset, 80));
      scheduledTimers.push(window.setTimeout(updateFloatBallOffset, 320));
    };

    updateFloatOffset();
    window.addEventListener("resize", updateFloatOffset);
    window.addEventListener("orientationchange", updateFloatOffset);
    document.addEventListener("fullscreenchange", updateFloatOffset);
    document.addEventListener("webkitfullscreenchange", updateFloatOffset);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateFloatOffset);
    }

    return () => {
      window.removeEventListener("resize", updateFloatOffset);
      window.removeEventListener("orientationchange", updateFloatOffset);
      document.removeEventListener("fullscreenchange", updateFloatOffset);
      document.removeEventListener("webkitfullscreenchange", updateFloatOffset);
      scheduledTimers.forEach((timer) => window.clearTimeout(timer));
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateFloatOffset);
      }
    };
  }, [gameMode, updateFloatBallOffset]);

  useEffect(() => {
    const restoreFromExternalGame = () => {
      syncGameMode();
      if (document.visibilityState === "hidden") {
        return;
      }
      if (!consumeCustomerServiceExternalGameReturn()) {
        return;
      }

      Toast.clear(true);
      chatServiceUnavailableRef.current = false;
      setVisible(false);
    };

    restoreFromExternalGame();
    window.addEventListener("pageshow", restoreFromExternalGame);
    window.addEventListener("focus", restoreFromExternalGame);
    document.addEventListener("visibilitychange", restoreFromExternalGame);

    return () => {
      window.removeEventListener("pageshow", restoreFromExternalGame);
      window.removeEventListener("focus", restoreFromExternalGame);
      document.removeEventListener("visibilitychange", restoreFromExternalGame);
    };
  }, [syncGameMode]);

  useEffect(() => {
    if (!chatCacheKey) {
      return;
    }
    if (skipNextCacheWriteRef.current) {
      skipNextCacheWriteRef.current = false;
      return;
    }
    writeChatCache(chatCacheKey, messages);
  }, [chatCacheKey, messages]);

  const mergeAndStoreMessages = useCallback((incomingMessages) => {
    if (!incomingMessages || incomingMessages.length === 0) {
      return;
    }

    setMessages((prevMessages) => {
      const merged = mergeMessages(prevMessages, incomingMessages);
      maxMsgIdRef.current = getMaxMessageId(merged);
      minMsgIdRef.current = getMinMessageId(merged);
      return merged;
    });
  }, []);

  const fetchHistory = useCallback(
    async (sinceMsgId, loopMore) => {
      if (loadingHistoryRef.current || chatServiceUnavailableRef.current) {
        return;
      }
      loadingHistoryRef.current = true;
      setLoadingHistory(true);

      try {
        const response = parseCustomerChatResponse(
          await SendCMD(
            CHAT_HISTORY_CMD,
            buildRequest({
              since_msg_id: sinceMsgId,
              limit: HISTORY_LIMIT,
            })
          )
        );

        if (!response.ok) {
          if (await handleSessionExpired(response)) {
            return;
          }
          Toast.fail(response.error || "Get history failed");
          return;
        }

        const res = response.data;
        const normalizedMessages = getHistoryMessages(res).map(normalizeMessage);
        mergeAndStoreMessages(normalizedMessages);

        const newMaxMsgId = Math.max(maxMsgIdRef.current, getMaxMessageId(normalizedMessages));
        maxMsgIdRef.current = newMaxMsgId;
        const incomingMinMsgId = getMinMessageId(normalizedMessages);
        if (incomingMinMsgId) {
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, incomingMinMsgId) : incomingMinMsgId;
        }
        if (!sinceMsgId) {
          hasMoreBeforeRef.current = getResponseHasMore(res.has_more, normalizedMessages.length);
        }
        setHistoryLoaded(true);

        if (loopMore && isHasMore(res.has_more) && normalizedMessages.length > 0) {
          setTimeout(() => {
            fetchHistory(newMaxMsgId, true);
          }, 0);
        }
      } catch (error) {
        console.error("customer chat history failed", error);
        if (await handleSessionExpired(error)) {
          return;
        }
        if (error && error.response && error.response.status === 404) {
          chatServiceUnavailableRef.current = true;
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          Toast.fail("Customer service API not found");
          return;
        }
        Toast.fail("Get history failed");
      } finally {
        loadingHistoryRef.current = false;
        setLoadingHistory(false);
      }
    },
    [buildRequest, handleSessionExpired, mergeAndStoreMessages]
  );

  const fetchHistoryBefore = useCallback(async () => {
    const beforeMsgId = minMsgIdRef.current;
    if (
      !beforeMsgId ||
      !hasMoreBeforeRef.current ||
      loadingHistoryBeforeRef.current ||
      chatServiceUnavailableRef.current
    ) {
      return;
    }

    const chatBody = chatBodyRef.current;
    const previousScrollHeight = chatBody ? chatBody.scrollHeight : 0;
    const previousScrollTop = chatBody ? chatBody.scrollTop : 0;
    loadingHistoryBeforeRef.current = true;

    try {
      const response = parseCustomerChatResponse(
        await SendCMD(
          CHAT_HISTORY_BEFORE_CMD,
          buildRequest({
            before_msg_id: beforeMsgId,
            limit: HISTORY_LIMIT,
          })
        )
      );

      if (!response.ok) {
        if (await handleSessionExpired(response)) {
          return;
        }
        Toast.fail(response.error || "Get history failed");
        return;
      }

      const res = response.data;
      const normalizedMessages = getHistoryMessages(res).map(normalizeMessage);
      if (!normalizedMessages.length) {
        hasMoreBeforeRef.current = false;
        return;
      }

      skipNextScrollToBottomRef.current = true;
      mergeAndStoreMessages(normalizedMessages);

      const incomingMinMsgId = getMinMessageId(normalizedMessages);
      if (incomingMinMsgId) {
        minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, incomingMinMsgId) : incomingMinMsgId;
      }
      hasMoreBeforeRef.current = getResponseHasMore(res.has_more, normalizedMessages.length);

      requestAnimationFrame(() => {
        const currentChatBody = chatBodyRef.current;
        if (currentChatBody && previousScrollHeight) {
          currentChatBody.scrollTop = currentChatBody.scrollHeight - previousScrollHeight + previousScrollTop;
        }
      });
    } catch (error) {
      console.error("customer chat history before failed", error);
      if (await handleSessionExpired(error)) {
        return;
      }
      Toast.fail("Get history failed");
    } finally {
      loadingHistoryBeforeRef.current = false;
    }
  }, [buildRequest, handleSessionExpired, mergeAndStoreMessages]);

  const handleChatScroll = useCallback(
    (event) => {
      if (event.currentTarget.scrollTop <= 8) {
        fetchHistoryBefore();
      }
    },
    [fetchHistoryBefore]
  );

  const gotoRegister = useCallback(() => {
    const backUrl = location.pathname + location.search;
    setPersist("login_back_url", backUrl);
    history.replace(
      ChannelRemind(`/login?reg=1&back_to=${encodeURIComponent(backUrl)}`)
    );
  }, [history, location.pathname, location.search, setPersist]);

  const openPopup = useCallback(() => {
    const currentUserInfo = getPersist("user_info");
    if (!currentUserInfo || !currentUserInfo.token) {
      gotoRegister();
      return;
    }
    chatServiceUnavailableRef.current = false;
    setVisible(true);
  }, [getPersist, gotoRegister]);

  const closeToFloat = () => {
    setVisible(false);
  };

  const copyDownloadApkUrl = async () => {
    if (!downloadApkUrl) {
      Toast.fail("Download link not ready");
      return;
    }

    const copied = await copyTextToClipboard(downloadApkUrl);
    if (copied) {
      Toast.success("Download link copied");
      return;
    }
    Toast.fail("Copy failed");
  };

  const appendLocalMessage = (message) => {
    setMessages((prevMessages) => sortMessages([...prevMessages, message]));
  };

  const updateLocalMessage = (clientTime, update) => {
    setMessages((prevMessages) =>
      sortMessages(
        prevMessages.map((message) => {
          if (message.client_time === clientTime) {
            return { ...message, ...update };
          }
          return message;
        })
      )
    );
  };

  const sendChatMessage = async ({ content, msgType, extra, previewContent }) => {
    if (!content) {
      return;
    }
    if (sendLimited) {
      Toast.info("Please wait for customer service reply");
      return;
    }

    const clientTime = Date.now();
    appendLocalMessage({
      local_id: `local-${clientTime}`,
      client_time: clientTime,
      sender_type: 0,
      sender_name: "Me",
      content: previewContent || content,
      server_content: content,
      msg_type: msgType,
      extra: extra || "",
      timestamp: Math.floor(clientTime / 1000),
      status: "pending",
    });

    setSending(true);
    try {
      const response = parseCustomerChatResponse(
        await SendCMD(
          CHAT_SEND_CMD,
          buildRequest({
            content,
            msg_type: msgType,
            extra: extra || "",
            client_time: clientTime,
          })
        )
      );

      if (response.ok) {
        const res = response.data;
        updateLocalMessage(clientTime, {
          msg_id: res.msg_id ? Number(res.msg_id) : 0,
          content,
          timestamp: normalizeTimestamp(res.timestamp),
          status: "sent",
        });
        if (res.msg_id) {
          const msgId = Number(res.msg_id);
          maxMsgIdRef.current = Math.max(maxMsgIdRef.current, msgId);
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, msgId) : msgId;
        }
        return;
      }

      updateLocalMessage(clientTime, { status: "failed" });
      if (await handleSessionExpired(response)) {
        return;
      }
      Toast.fail(response.error || "Send failed");
    } catch (error) {
      console.error("customer chat send failed", error);
      updateLocalMessage(clientTime, { status: "failed" });
      if (await handleSessionExpired(error)) {
        return;
      }
      Toast.fail("Send failed");
    } finally {
      setSending(false);
    }
  };

  const sendTextMessage = () => {
    const content = inputValue.trim();
    if (!content) {
      return;
    }
    setInputValue("");
    sendChatMessage({
      content,
      msgType: MESSAGE_TYPE_TEXT,
    });
  };

  const uploadImageFile = async (file) => {
    const fileExtension = getFileExtension(file);
    if (!fileExtension) {
      Toast.info("Only jpg/png images are supported");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      Toast.info("Image must be less than 5MB");
      return;
    }
    if (sendLimited) {
      Toast.info("Please wait for customer service reply");
      return;
    }

    let previewUrl = "";
    let imageSize = {};
    try {
      [previewUrl, imageSize] = await Promise.all([getImagePreview(file), getImageSize(file)]);
    } catch (error) {
      console.error("customer chat image preview failed", error);
      Toast.fail("Image preview failed");
      return;
    }
    const extra = JSON.stringify(imageSize);
    const clientTime = Date.now();

    appendLocalMessage({
      local_id: `local-${clientTime}`,
      client_time: clientTime,
      sender_type: 0,
      sender_name: "Me",
      content: previewUrl,
      msg_type: MESSAGE_TYPE_IMAGE,
      extra,
      timestamp: Math.floor(clientTime / 1000),
      status: "uploading",
    });
    setUploading(true);
    try {
      const uploadResponse = parseCustomerChatResponse(
        await SendCMD(
          CHAT_IMAGE_UPLOAD_CMD,
          buildRequest({
            file_extension: fileExtension,
          })
        )
      );

      const uploadRes = uploadResponse.data;
      if (!uploadResponse.ok || !uploadRes.upload_url || !uploadRes.image_url) {
        updateLocalMessage(clientTime, { status: "failed" });
        if (await handleSessionExpired(uploadResponse)) {
          return;
        }
        Toast.fail(uploadResponse.error || "Image upload failed");
        return;
      }
      const uploadUrl = normalizeUploadUrl(uploadRes.upload_url);
      const contentType = getImageContentType(file, fileExtension);
      const putRes = await putImageToUploadUrl(uploadUrl, file, contentType);
      if (!putRes.ok) {
        updateLocalMessage(clientTime, { status: "failed" });
        console.error("customer chat image put failed", putRes.status, putRes.statusText);
        Toast.fail("Image upload failed");
        return;
      }

      updateLocalMessage(clientTime, {
        content: uploadRes.image_url,
        msg_type: MESSAGE_TYPE_IMAGE,
        status: "pending",
      });

      const sendResponse = parseCustomerChatResponse(
        await SendCMD(
          CHAT_SEND_CMD,
          buildRequest({
            content: uploadRes.image_url,
            msg_type: MESSAGE_TYPE_IMAGE,
            extra,
            client_time: clientTime,
          })
        )
      );

      if (sendResponse.ok) {
        const sendRes = sendResponse.data;
        updateLocalMessage(clientTime, {
          msg_id: sendRes.msg_id ? Number(sendRes.msg_id) : 0,
          content: uploadRes.image_url,
          msg_type: MESSAGE_TYPE_IMAGE,
          timestamp: normalizeTimestamp(sendRes.timestamp),
          status: "sent",
        });
        if (sendRes.msg_id) {
          const msgId = Number(sendRes.msg_id);
          maxMsgIdRef.current = Math.max(maxMsgIdRef.current, msgId);
          minMsgIdRef.current = minMsgIdRef.current ? Math.min(minMsgIdRef.current, msgId) : msgId;
        }
        return;
      }

      updateLocalMessage(clientTime, { status: "failed" });
      if (await handleSessionExpired(sendResponse)) {
        return;
      }
      Toast.fail(sendResponse.error || "Send failed");
    } catch (error) {
      console.error("customer chat image failed", error);
      updateLocalMessage(clientTime, { status: "failed" });
      if (await handleSessionExpired(error)) {
        return;
      }
      Toast.fail("Image upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    const handleOpen = () => {
      openPopup();
    };
    window.addEventListener(CUSTOMER_SERVICE_OPEN_EVENT, handleOpen);
    return () => {
      window.removeEventListener(CUSTOMER_SERVICE_OPEN_EVENT, handleOpen);
    };
  }, [openPopup]);

  useEffect(() => {
    if (!visible) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return undefined;
    }

    fetchHistory(historyLoaded ? maxMsgIdRef.current : 0, false);
    pollTimerRef.current = setInterval(() => {
      fetchHistory(maxMsgIdRef.current, false);
    }, POLL_INTERVAL);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchHistory, historyLoaded, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (skipNextScrollToBottomRef.current) {
      skipNextScrollToBottomRef.current = false;
      return;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView(false);
    }
  }, [messages.length, visible]);

  const customerServiceContent = (
    <>
      <Popup
        visible={visible}
        className={`${style.customer_service_popup} ${gameMode ? style.customer_service_popup_game : ""}`}
        closeOnClickOverlay={false}
        onClose={closeToFloat}
      >
        <div class={style.service_window}>
          <button class={style.close_btn} onClick={closeToFloat}>
            x
          </button>
          <div class={style.chat_shell}>
            <div class={style.chat_profile}>
              <img src={require("@/assets/images/home/kftb_1.png").default} />
              <div class={style.chat_profile_content}>
                <div class={style.chat_profile_name}>{LocalConfig.gameName} Customer Service</div>
                <div class={style.chat_profile_desc}>App must be updated to the latest package to upload images.</div>
                {downloadApkUrl ? (
                  <div class={style.chat_profile_download_row}>
                    <a class={style.chat_profile_download} href={downloadApkUrl} target="_blank" rel="noreferrer">
                      Download latest APK
                    </a>
                    <button class={style.chat_profile_copy} type="button" title="Copy download link" onClick={copyDownloadApkUrl}>
                      <img src={CopyIcon} alt="" />
                      <span>Copy</span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div class={style.chat_body} ref={chatBodyRef} onScroll={handleChatScroll}>
              {loadingHistory && !historyLoaded && messages.length === 0 ? (
                <div class={style.chat_loading}>
                  <div class={style.loading_dots}>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              ) : null}
              {messages.map((message) => {
                const isMine = isMyMessage(message, userInfo);
                return (
                  <div
                    key={message.msg_id || message.local_id || message.client_time}
                    class={`${style.message_row} ${isMine ? style.message_row_mine : ""}`}
                  >
                    {!isMine ? (
                      <div class={style.bot_name_row}>
                        <img src={require("@/assets/images/home/kftb_1.png").default} />
                        <span>{message.sender_name || (message.sender_type === 2 ? "System" : "Bot")}</span>
                      </div>
                    ) : null}
                    <div class={`${style.message_bubble} ${isMine ? style.message_bubble_mine : ""}`}>
                      {isImageMessage(message) ? (
                        <img class={style.message_image} src={message.content} />
                      ) : (
                        <p>{message.content}</p>
                      )}
                      <div class={style.message_meta}>
                        <span>{formatMessageTime(message.timestamp)}</span>
                        {message.status === "pending" || message.status === "uploading" ? <span>Sending</span> : null}
                        {message.status === "failed" ? <span>Failed</span> : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div class={style.chat_input_area}>
              <input
                value={inputValue}
                placeholder={sendLimited ? "Please wait for customer service reply" : "Input message"}
                disabled={inputDisabled}
                onInput={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    sendTextMessage();
                  }
                }}
              />
              {sendLimited ? <div class={style.send_limit_tip}>Please wait for customer service reply</div> : null}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = "";
                  if (file) {
                    uploadImageFile(file);
                  }
                }}
              />
              <div class={style.chat_input_tools}>
                <button
                  class={style.tool_button}
                  aria-label="Send image"
                  title="Send image"
                  disabled={sendDisabled}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.click();
                    }
                  }}
                >
                  <span class={style.image_tool_icon} />
                </button>
                <button
                  class={`${style.send_button} ${inputValue.trim() && !sendDisabled ? style.send_active : ""}`}
                  disabled={!inputValue.trim() || sendDisabled}
                  onClick={sendTextMessage}
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </Popup>
      {!visible ? (
        <div class={`${style.float_ball_container} customer-service-float-ball ${gameMode ? style.float_ball_container_game : ""}`}>
          <FloatingBall
            key={`customer-service-${floatBallResetKey}-${floatBallOffset.right}-${floatBallOffset.bottom}`}
            offset={floatBallOffset}
            adsorb={{
              distance: 15,
            }}
            onClick={openPopup}
          >
            <div class={style.service_float_ball} role="button" aria-label="Customer service">
              <img class={style.service_float_icon} src={CustomerServiceFloatIcon} alt="" />
            </div>
          </FloatingBall>
        </div>
      ) : null}
    </>
  );

  return portalContainer ? createPortal(customerServiceContent, portalContainer) : customerServiceContent;
};

export default CustomerServiceWidget;
